import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Box } from '@gonfalon/observability-ui/components/Box';
import {
  IconSolidCheveronDown,
  IconSolidCheveronUp,
  IconSolidSearch,
} from '@gonfalon/observability-ui/components/icons';
import { Text } from '@gonfalon/observability-ui/components/Text';
import { Icon } from '@launchpad-ui/icons';

import {
  chevronIcon,
  container,
  containerExpanded,
  containerInteractive,
  expandedContent,
  hoveredOpacity,
  icon,
  innerContent,
  innerDivider,
  label,
  labelLoading,
  minWidthZero,
  toolDetailsContent,
  toolDetailsIcon,
  toolDetailsPre,
  toolDetailsToggle,
} from './ShimmerText.css';

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ');

/**
 * ShimmerText component - displays loading animation and expandable tool results.
 * Accepts a `renderContent` thunk that is only invoked when the panel is expanded,
 * so callers can avoid expensive parsing/rendering for collapsed tools.
 */
export const ShimmerText: FC<{
  text: string;
  isLoading?: boolean;
  toolInputs?: any;
  onClick?: () => void;
  renderContent?: () => React.ReactNode;
  startExpanded?: boolean;
}> = ({ text, isLoading = false, toolInputs, onClick, renderContent, startExpanded = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(startExpanded);
  const [showToolDetails, setShowToolDetails] = useState(false);
  const [rotatingText, setRotatingText] = useState(text);

  // One-directional sync: when startExpanded flips to true (e.g. content arrives
  // mid-mount and the parent's auto-expand check now passes), open the panel.
  // Never force-closes — preserves the user's ability to manually collapse.
  useEffect(() => {
    if (startExpanded) {
      setIsExpanded(true);
    }
  }, [startExpanded]);

  // Bash/command tools with no output: show command only, not expandable
  const commandFromInputs =
    toolInputs && typeof (toolInputs as { command?: string }).command === 'string'
      ? (toolInputs as { command: string }).command
      : null;
  const hasContent = !!renderContent;
  const isCommandWithNoOutput = !!commandFromInputs && !hasContent;
  const isInteractive = hasContent && !isCommandWithNoOutput;
  const isThinking = text === 'Thinking...';

  // Fun loading text options
  const loadingTexts = [
    'Thinking',
    'Observing',
    'Searching',
    'Fizzbuzzing',
    'Analyzing',
    'Investigating',
    'Pondering',
    'Computing',
    'Exploring',
    'Discovering',
  ];

  // Rotate text every 2 seconds when loading and text is "Thinking..."
  useEffect(() => {
    if (isLoading && isThinking) {
      let currentIndex = 0;
      const interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % loadingTexts.length;
        setRotatingText(`${loadingTexts[currentIndex]}...`);
      }, 2000);

      return () => clearInterval(interval);
    } else {
      setRotatingText(text);
    }
  }, [isLoading, text]);

  const handleClick = () => {
    if (isInteractive) {
      setIsExpanded(!isExpanded);
      onClick?.();
    }
  };

  return (
    <Box marginBottom="12" marginTop="6">
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        cssClass={cx(container, isInteractive && containerInteractive, isExpanded && containerExpanded)}
        onClick={handleClick}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap="4">
            {(() => {
              if (isThinking) {
                return <IconSolidSearch className={icon} />;
              } else if (!isInteractive) {
                return null;
              }

              const chevronClassName = cx(chevronIcon, isHovered && hoveredOpacity);

              return isExpanded ? (
                <IconSolidCheveronDown className={chevronClassName} />
              ) : (
                <IconSolidCheveronUp className={chevronClassName} />
              );
            })()}
            <Box display="flex" flexDirection="column" gap="2" cssClass={minWidthZero}>
              <span className={cx(label, isInteractive && isHovered && hoveredOpacity, isLoading && labelLoading)}>
                {rotatingText}
              </span>
              {isCommandWithNoOutput && commandFromInputs && (
                <Text size="small" color="weak" family="monospace" lines="2" title={commandFromInputs}>
                  {commandFromInputs}
                </Text>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Rendered content when expanded */}
      {isExpanded && renderContent && (
        <Box marginTop="0" px="8" pb="8" cssClass={expandedContent}>
          <Box cssClass={innerContent}>{renderContent()}</Box>

          {/* Divider and show tool inputs toggle at the bottom of expanded card */}
          {toolInputs && (
            <>
              <Box cssClass={innerDivider} />
              <Box
                display="inline-flex"
                alignItems="center"
                gap="4"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setShowToolDetails(!showToolDetails);
                }}
                cssClass={toolDetailsToggle}
              >
                <Icon name="code-circle-outline" size="small" className={toolDetailsIcon} />
                <Text size="small" color="weak" weight="medium">
                  {showToolDetails ? 'Hide tool inputs' : 'Show tool inputs'}
                </Text>
              </Box>
              {showToolDetails && (
                <Box marginTop="8" cssClass={toolDetailsContent}>
                  <pre className={toolDetailsPre}>{JSON.stringify(toolInputs, null, 2)}</pre>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
